<template>
  <div class="effect-report-content">
    <section class="summary-grid" aria-label="月报概览">
      <article class="summary-card">
        <div class="card-head">
          <h2 class="card-title">关键词</h2>
          <span class="source-tag">GSC</span>
        </div>
        <div class="metric-grid metric-grid--three">
          <div
            v-for="metric in keywordMetrics"
            :key="metric.key"
            class="metric"
          >
            <div class="metric-label">{{ metric.label }}</div>
            <div class="metric-value">{{ formatNumber(metric.value) }}</div>
            <span
              v-if="metric.change !== null"
              :class="changeClass(metric.change)"
            >
              {{ changeText(metric.change) }}
            </span>
          </div>
        </div>
        <keyword-trend-chart
          class="summary-chart"
          :trend="summary.keywords.trend || []"
        />
      </article>

      <article class="summary-card">
        <div class="card-head">
          <h2 class="card-title">活跃用户</h2>
          <span class="source-tag source-tag--ga">GA4</span>
        </div>
        <div class="metric-grid metric-grid--three">
          <div v-for="metric in userMetrics" :key="metric.key" class="metric">
            <div class="metric-label">{{ metric.label }}</div>
            <div class="metric-value">{{ formatNumber(metric.value) }}</div>
            <span
              v-if="metric.change !== null"
              :class="changeClass(metric.change)"
            >
              {{ changeText(metric.change) }}
            </span>
          </div>
        </div>
        <user-trend-chart
          class="summary-chart"
          :trend="summary.users.trend || []"
        />
      </article>

      <article class="summary-card summary-card--traffic">
        <div class="card-head">
          <h2 class="card-title">数据流量</h2>
          <span class="source-tag">GSC</span>
        </div>
        <div class="metric-grid metric-grid--four">
          <div
            v-for="metric in trafficMetrics"
            :key="metric.key"
            class="metric"
          >
            <div class="metric-label">{{ metric.label }}</div>
            <div class="metric-value">
              {{ formatMetric(metric.value, metric.format) }}
            </div>
            <span
              v-if="metric.change !== null"
              :class="changeClass(metric.change, metric.key)"
            >
              {{ changeText(metric.change, metric.format, metric.key) }}
            </span>
          </div>
        </div>
        <traffic-trend-chart
          class="summary-chart summary-chart--traffic"
          :trend="summary.traffic.trend || []"
        />
      </article>

      <article class="summary-card">
        <div class="card-head">
          <h2 class="card-title">询盘数量</h2>
          <span class="source-tag source-tag--lead">表单</span>
        </div>
        <div class="metric-grid metric-grid--three">
          <div
            v-for="(item, index) in leadTrend"
            :key="item.stat_month"
            class="metric"
          >
            <div class="metric-label">{{ item.label }}询盘</div>
            <div class="metric-value">{{ formatNumber(item.count) }}</div>
            <span
              v-if="leadChange(index) !== null"
              :class="changeClass(leadChange(index), '', false)"
            >
              {{ signedNumber(leadChange(index)) }}
            </span>
          </div>
        </div>
        <lead-trend-chart class="summary-chart" :trend="leadTrend" />
      </article>
    </section>

    <section class="content-grid" aria-label="来源分布">
      <article class="panel">
        <div class="panel-head">
          <div>
            <h2 class="panel-title">国家来源 Top 10</h2>
            <div class="panel-note">按会话数统计</div>
          </div>
          <span class="small-badge">GA4</span>
        </div>
        <country-pie-chart
          class="panel-chart panel-chart--country"
          :rows="report.countries || []"
        />
      </article>

      <article class="panel">
        <div class="panel-head">
          <div>
            <h2 class="panel-title">渠道来源 Top 10</h2>
            <div class="panel-note">来源 / 媒介</div>
          </div>
          <span class="small-badge">GA4</span>
        </div>
        <channel-bar-chart class="panel-chart" :rows="report.channels || []" />
      </article>
    </section>

    <section class="content-grid" aria-label="热门内容">
      <article class="panel">
        <div class="panel-head panel-head--table">
          <h2 class="panel-title">热门搜索词 Top 100</h2>
          <div class="panel-head__tools">
            <el-radio-group v-model="querySort" size="mini">
              <el-radio-button label="impressions">曝光</el-radio-button>
              <el-radio-button label="position">排名</el-radio-button>
            </el-radio-group>
            <span class="small-badge">GSC</span>
          </div>
        </div>
        <div class="table-wrap">
          <table class="report-table">
            <thead>
              <tr>
                <th>序号</th>
                <th>搜索词</th>
                <th class="number">平均排名</th>
                <th class="number">曝光量</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in visibleQueries" :key="item.query">
                <td>{{ index + 1 }}</td>
                <td class="keyword" :title="item.query">{{ item.query }}</td>
                <td class="number">{{ formatPosition(item.position) }}</td>
                <td class="number">{{ formatNumber(item.impressions) }}</td>
              </tr>
              <tr v-if="!visibleQueries.length">
                <td colspan="4" class="empty-cell">暂无搜索词数据</td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>

      <article class="panel">
        <div class="panel-head panel-head--table">
          <h2 class="panel-title">热门页面 Top 100</h2>
          <div class="panel-head__tools">
            <el-radio-group v-model="pageSort" size="mini">
              <el-radio-button label="impressions">曝光</el-radio-button>
              <el-radio-button label="position">排名</el-radio-button>
            </el-radio-group>
            <span class="small-badge">GSC</span>
          </div>
        </div>
        <div class="table-wrap">
          <table class="report-table">
            <thead>
              <tr>
                <th>序号</th>
                <th>页面</th>
                <th class="number">平均排名</th>
                <th class="number">曝光量</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in visiblePages" :key="item.page">
                <td>{{ index + 1 }}</td>
                <td class="url">
                  <a
                    :href="item.page"
                    :title="item.page"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ item.display_path || item.page }}
                  </a>
                </td>
                <td class="number">{{ formatPosition(item.position) }}</td>
                <td class="number">{{ formatNumber(item.impressions) }}</td>
              </tr>
              <tr v-if="!visiblePages.length">
                <td colspan="4" class="empty-cell">暂无页面数据</td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>

      <div
        v-if="hasMorePopularContent"
        class="table-action table-action--shared"
      >
        <el-button type="text" @click="togglePopularContent">
          <span v-if="popularContentExpanded" key="collapse">收起</span>
          <span v-else key="expand">查看更多</span>
          <i
            :class="
              popularContentExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'
            "
          ></i>
        </el-button>
      </div>
    </section>

    <section class="panel panel--full" aria-label="优化主推词">
      <div class="panel-head">
        <div>
          <h2 class="panel-title">优化主推词</h2>
          <div class="panel-note">主词排名：DataForSEO · 曝光量：GSC</div>
        </div>
        <span class="small-badge">主词</span>
      </div>
      <div class="table-wrap">
        <table class="report-table">
          <thead>
            <tr>
              <th>序号</th>
              <th>关键词</th>
              <th class="number">排名</th>
              <th class="number">曝光量</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in report.promoted_keywords || []"
              :key="item.keyword_id"
            >
              <td>{{ index + 1 }}</td>
              <td class="keyword" :title="item.keyword">{{ item.keyword }}</td>
              <td class="number rank-value">
                {{ formatNumber(item.rank_position) }}
              </td>
              <td class="number">{{ formatNumber(item.gsc_impressions) }}</td>
            </tr>
            <tr v-if="!(report.promoted_keywords || []).length">
              <td colspan="4" class="empty-cell">暂无优化主推词</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="content-grid content-grid--three" aria-label="运营数据">
      <article
        v-for="item in report.operation_stats || []"
        :key="item.metric_type"
        class="panel"
      >
        <div class="panel-head">
          <h2 class="panel-title">{{ item.label }}</h2>
        </div>
        <div class="operation-stat">
          <div class="operation-stat__item">
            <div class="operation-stat__label">月更新数</div>
            <div class="operation-stat__value">
              {{ formatNumber(item.monthly_count) }}
            </div>
          </div>
          <div class="operation-stat__item">
            <div class="operation-stat__label">累计总数</div>
            <div class="operation-stat__value">
              {{ formatNumber(item.cumulative_count) }}
            </div>
          </div>
        </div>
        <operation-trend-chart
          class="panel-chart panel-chart--small"
          :trend="item.trend || []"
          :metric-type="item.metric_type"
        />
      </article>
    </section>

    <section class="content-grid" aria-label="优化记录">
      <article class="panel">
        <div class="panel-head">
          <h2 class="panel-title">本月优化记录</h2>
        </div>
        <div class="record-box">
          <template
            v-if="
              noteLines(report.notes && report.notes.current_summary).length
            "
          >
            <p
              v-for="(line, index) in noteLines(
                report.notes && report.notes.current_summary
              )"
              :key="index"
              class="record-line"
            >
              {{ line }}
            </p>
          </template>
          <div v-else class="record-empty">暂无记录</div>
        </div>
      </article>

      <article class="panel">
        <div class="panel-head">
          <h2 class="panel-title">下月优化计划</h2>
        </div>
        <div class="record-box">
          <template
            v-if="noteLines(report.notes && report.notes.next_plan).length"
          >
            <p
              v-for="(line, index) in noteLines(
                report.notes && report.notes.next_plan
              )"
              :key="index"
              class="record-line"
            >
              {{ line }}
            </p>
          </template>
          <div v-else class="record-empty">暂无计划</div>
        </div>
      </article>
    </section>

    <section class="panel panel--full" aria-label="Technical SEO">
      <div class="panel-head">
        <h2 class="panel-title">Technical SEO</h2>
        <span class="small-badge">
          已完成 {{ technical.completed_count || 0 }} /
          {{ technical.total_count || 0 }}
        </span>
      </div>
      <div class="technical-grid">
        <div
          v-for="item in technical.items || []"
          :key="item.item_key"
          :class="[
            'technical-item',
            { 'technical-item--pending': item.status !== 'completed' },
          ]"
        >
          <i
            :class="
              item.status === 'completed'
                ? 'el-icon-circle-check'
                : 'el-icon-time'
            "
            aria-hidden="true"
          ></i>
          <span :title="item.item_name">{{ item.item_name }}</span>
        </div>
      </div>
    </section>

    <section
      v-if="membersEnabled"
      class="panel panel--full"
      aria-label="项目成员"
    >
      <div class="panel-head">
        <h2 class="panel-title">项目成员</h2>
      </div>
      <div v-if="(report.members || []).length" class="member-table-wrapper">
        <table class="member-table">
          <tbody>
            <tr
              v-for="(member, index) in report.members"
              :key="`${member.role}-${member.name}-${index}`"
            >
              <td class="member-table__identity">
                <div class="member-cell">
                  <i class="el-icon-user-solid" aria-hidden="true"></i>
                  <span>{{ member.role }}：{{ member.name }}</span>
                </div>
              </td>
              <td class="member-table__phone">
                <div class="member-cell">
                  <i class="el-icon-phone" aria-hidden="true"></i>
                  <span>电话：{{ member.phone || "—" }}</span>
                </div>
              </td>
              <td class="member-table__email">
                <div class="member-cell">
                  <i class="el-icon-message" aria-hidden="true"></i>
                  <span>邮箱：{{ member.email || "—" }}</span>
                </div>
              </td>
              <td class="member-table__image">
                <div class="member-cell">
                  <i class="el-icon-picture-outline" aria-hidden="true"></i>
                  <img
                    v-if="member.contact_image_path"
                    :src="member.contact_image_path"
                    :alt="`${member.name}联系图片`"
                  />
                  <span v-else>联系图片：—</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="panel-empty">暂无项目成员</div>
    </section>
  </div>
</template>

<script>
import KeywordTrendChart from "./KeywordTrendChart.vue";
import UserTrendChart from "./UserTrendChart.vue";
import TrafficTrendChart from "./TrafficTrendChart.vue";
import LeadTrendChart from "./LeadTrendChart.vue";
import CountryPieChart from "./CountryPieChart.vue";
import ChannelBarChart from "./ChannelBarChart.vue";
import OperationTrendChart from "./OperationTrendChart.vue";

export default {
  name: "EffectReportContent",
  components: {
    KeywordTrendChart,
    UserTrendChart,
    TrafficTrendChart,
    LeadTrendChart,
    CountryPieChart,
    ChannelBarChart,
    OperationTrendChart,
  },
  props: {
    report: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      querySort: "impressions",
      pageSort: "impressions",
      popularContentExpanded: false,
      tablePreviewCount: 20,
    };
  },
  computed: {
    summary() {
      return {
        keywords: {},
        users: {},
        traffic: {},
        leads: {},
        ...(this.report.summary || {}),
      };
    },
    technical() {
      return (
        this.report.technical || {
          completed_count: 0,
          total_count: 0,
          items: [],
        }
      );
    },
    membersEnabled() {
      return (
        !Object.prototype.hasOwnProperty.call(this.report, "members_enabled") ||
        ![false, 0, "0"].includes(this.report.members_enabled)
      );
    },
    keywordMetrics() {
      const data = this.summary.keywords || {};
      const change = data.change || {};
      return [
        {
          key: "top10",
          label: "首页量",
          value: data.top10,
          change: change.top10,
        },
        {
          key: "top20",
          label: "前二页",
          value: data.top20,
          change: change.top20,
        },
        {
          key: "top30",
          label: "前三页",
          value: data.top30,
          change: change.top30,
        },
      ];
    },
    userMetrics() {
      const data = this.summary.users || {};
      const change = data.change || {};
      return [
        {
          key: "sessions",
          label: "会话数",
          value: data.sessions,
          change: change.sessions,
        },
        {
          key: "total_users",
          label: "用户总数",
          value: data.total_users,
          change: change.total_users,
        },
        {
          key: "new_users",
          label: "新用户数",
          value: data.new_users,
          change: change.new_users,
        },
      ];
    },
    trafficMetrics() {
      const data = this.summary.traffic || {};
      const change = data.change || {};
      return [
        {
          key: "clicks",
          label: "总点击数",
          value: data.clicks,
          change: change.clicks,
          format: "number",
        },
        {
          key: "impressions",
          label: "总曝光数",
          value: data.impressions,
          change: change.impressions,
          format: "number",
        },
        {
          key: "ctr",
          label: "平均 CTR",
          value: data.ctr,
          change: change.ctr,
          format: "percent",
        },
        {
          key: "avg_position",
          label: "平均排名",
          value: data.avg_position,
          change: change.avg_position,
          format: "position",
        },
      ];
    },
    leadTrend() {
      return (this.summary.leads && this.summary.leads.trend) || [];
    },
    reportIdentity() {
      const meta = this.report.meta || {};
      return `${meta.site_id || ""}:${meta.stat_month || ""}`;
    },
    sortedQueries() {
      return this.sortRows(this.report.queries || [], this.querySort);
    },
    visibleQueries() {
      return this.popularContentExpanded
        ? this.sortedQueries
        : this.sortedQueries.slice(0, this.tablePreviewCount);
    },
    sortedPages() {
      return this.sortRows(this.report.pages || [], this.pageSort);
    },
    visiblePages() {
      return this.popularContentExpanded
        ? this.sortedPages
        : this.sortedPages.slice(0, this.tablePreviewCount);
    },
    hasMorePopularContent() {
      return (
        this.sortedQueries.length > this.tablePreviewCount ||
        this.sortedPages.length > this.tablePreviewCount
      );
    },
  },
  watch: {
    reportIdentity() {
      this.popularContentExpanded = false;
    },
  },
  methods: {
    expandAll() {
      this.popularContentExpanded = true;
    },
    togglePopularContent() {
      this.popularContentExpanded = !this.popularContentExpanded;
    },
    sortRows(rows, field) {
      return rows.slice().sort((left, right) => {
        if (field === "position") {
          const leftValue =
            left.position === null
              ? Number.MAX_SAFE_INTEGER
              : Number(left.position);
          const rightValue =
            right.position === null
              ? Number.MAX_SAFE_INTEGER
              : Number(right.position);
          return leftValue - rightValue;
        }
        return Number(right.impressions || 0) - Number(left.impressions || 0);
      });
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "—";
      return Number(value).toLocaleString("zh-CN");
    },
    formatPosition(value) {
      if (value === null || value === undefined || value === "") return "—";
      return Number(value).toFixed(2);
    },
    formatMetric(value, format) {
      if (format === "percent") {
        return value === null || value === undefined
          ? "—"
          : `${(Number(value) * 100).toFixed(2)}%`;
      }
      if (format === "position") return this.formatPosition(value);
      return this.formatNumber(value);
    },
    signedNumber(value) {
      if (value === null || value === undefined) return "";
      return `${Number(value) > 0 ? "+" : ""}${this.formatNumber(value)}`;
    },
    changeText(value, format = "number", key = "") {
      if (value === null || value === undefined) return "";
      if (key === "avg_position") {
        if (Number(value) === 0) return "较上月持平";
        return Number(value) > 0
          ? `改善 ${Math.abs(Number(value)).toFixed(2)} 位`
          : `下降 ${Math.abs(Number(value)).toFixed(2)} 位`;
      }
      if (format === "percent") {
        const point = (Number(value) * 100).toFixed(2);
        return `较上月 ${Number(value) > 0 ? "+" : ""}${point} 个百分点`;
      }
      return `较上月 ${this.signedNumber(value)}`;
    },
    changeClass(value, key = "", includeBase = true) {
      const base = includeBase ? "metric-change " : "metric-change ";
      if (Number(value) === 0) return `${base}is-flat`;
      if (key === "avg_position") {
        return `${base}${Number(value) > 0 ? "is-up" : "is-down"}`;
      }
      return `${base}${Number(value) > 0 ? "is-up" : "is-down"}`;
    },
    leadChange(index) {
      if (index <= 0 || !this.leadTrend[index - 1]) return null;
      return (
        Number(this.leadTrend[index].count || 0) -
        Number(this.leadTrend[index - 1].count || 0)
      );
    },
    noteLines(value) {
      return String(value || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    },
  },
};
</script>

<style lang="scss" scoped>
.effect-report-content {
  --panel-border: #e6edf6;
  --text-primary: #18263d;
  --text-secondary: #607089;
  --text-muted: #8a97aa;
  --primary: #2f7cf6;
  --success: #18b884;
  --danger: #e64a55;
  --shadow: 0 8px 22px rgba(34, 50, 74, 0.045);
  width: 100%;
  color: var(--text-primary);
  letter-spacing: 0;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 14px;
}

.panel,
.summary-card {
  min-width: 0;
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  background: #fff;
  box-shadow: var(--shadow);
}

.summary-card {
  min-height: 342px;
  overflow: hidden;
  padding: 16px;
}

.card-head,
.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.card-head {
  margin-bottom: 14px;
}

.panel-head {
  align-items: center;
  margin-bottom: 12px;
}

.panel-head--table {
  min-height: 29px;
}

.panel-head__tools {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-title,
.panel-title {
  margin: 0;
  color: var(--text-primary);
  font-weight: 700;
  letter-spacing: 0;
}

.card-title {
  font-size: 16px;
  line-height: 22px;
}

.panel-title {
  font-size: 15px;
  line-height: 21px;
}

.source-tag,
.small-badge {
  flex: 0 0 auto;
  border-radius: 999px;
  background: #eef5ff;
  padding: 3px 9px;
  color: var(--primary);
  font-size: 11px;
  font-weight: 600;
}

.source-tag--ga {
  background: #fff7e6;
  color: #d97904;
}

.source-tag--lead {
  background: #e9fbf5;
  color: #15956a;
}

.metric-grid {
  display: grid;
  gap: 0;
  min-height: 72px;
  margin-bottom: 10px;
}

.metric-grid--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.metric-grid--four {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  row-gap: 12px;
}

.metric {
  min-width: 0;
  border-left: 1px solid #edf1f7;
  padding: 0 10px;
}

.metric:first-child,
.metric-grid--four .metric:nth-child(odd) {
  border-left: 0;
  padding-left: 0;
}

.metric-grid--three .metric:last-child,
.metric-grid--four .metric:nth-child(even) {
  padding-right: 0;
}

.metric-label {
  overflow: hidden;
  color: var(--text-muted);
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-value {
  margin-top: 4px;
  overflow: hidden;
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-change {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin-top: 4px;
  border-radius: 999px;
  background: #f4f6f8;
  padding: 2px 7px;
  color: var(--text-muted);
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
}

.metric-change.is-up {
  background: #e9fbf5;
  color: #18a979;
}

.metric-change.is-down {
  background: #fff1f0;
  color: var(--danger);
}

.summary-chart {
  width: 100%;
  height: 180px;
}

.summary-chart--traffic {
  height: 145px;
}

.content-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 14px;
}

.content-grid--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.panel {
  padding: 16px;
}

.panel--full {
  margin-bottom: 14px;
}

.panel-note {
  margin-top: 3px;
  color: var(--text-muted);
  font-size: 12px;
}

.panel-chart {
  width: 100%;
  height: 300px;
}

.panel-chart--small {
  height: 210px;
}

.panel-chart--country {
  height: 300px;
}

.table-wrap {
  width: 100%;
  overflow-x: auto;
}

.report-table {
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  table-layout: fixed;
}

.report-table th,
.report-table td {
  border-bottom: 1px solid #ebeef5;
  padding: 11px 10px;
  text-align: left;
  vertical-align: middle;
}

.report-table th {
  background: #f7f9fc;
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
}

.report-table td {
  color: #31415a;
  font-size: 13px;
}

.report-table tbody tr:hover td {
  background: #f8fbff;
}

.report-table th:first-child,
.report-table td:first-child {
  width: 52px;
  text-align: center;
}

.report-table .number {
  width: 92px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.report-table .url,
.report-table .keyword {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-table a {
  color: #31415a;
  text-decoration: none;
}

.report-table a:hover {
  color: var(--primary);
}

.empty-cell {
  height: 100px;
  color: #a2adbc !important;
  text-align: center !important;
}

.table-action {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.table-action--shared {
  grid-column: 1 / -1;
  margin-top: -4px;
}

.rank-value {
  color: var(--primary) !important;
  font-weight: 700;
}

.operation-stat {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 4px;
  border-radius: 8px;
  background: #f8fafc;
  padding: 12px;
}

.operation-stat__item {
  text-align: center;
}

.operation-stat__item + .operation-stat__item {
  border-left: 1px solid #e7edf5;
}

.operation-stat__label {
  color: var(--text-muted);
  font-size: 12px;
}

.operation-stat__value {
  margin-top: 4px;
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 700;
}

.record-box {
  min-height: 168px;
  border: 1px solid #edf1f7;
  border-radius: 8px;
  background: #fbfdff;
  padding: 14px 16px;
}

.record-line {
  position: relative;
  margin: 0;
  padding: 7px 0 7px 16px;
  color: #43536a;
  line-height: 20px;
  white-space: pre-wrap;
  word-break: break-word;
}

.record-line::before {
  position: absolute;
  top: 15px;
  left: 1px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary);
  content: "";
}

.record-empty,
.panel-empty {
  display: flex;
  min-height: 120px;
  align-items: center;
  justify-content: center;
  color: #a2adbc;
  font-size: 13px;
}

.technical-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

.technical-item {
  display: flex;
  min-width: 0;
  min-height: 44px;
  align-items: center;
  border: 1px solid #e8eef6;
  border-radius: 8px;
  background: #fbfdff;
  padding: 10px 12px;
  color: #43536a;
  font-size: 13px;
}

.technical-item i {
  flex: 0 0 auto;
  margin-right: 8px;
  color: var(--success);
  font-size: 16px;
}

.technical-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.technical-item--pending {
  color: #8a97aa;
}

.technical-item--pending i {
  color: #aeb9c8;
}

.member-table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.member-table {
  width: 100%;
  min-width: 760px;
  border: 1px solid #e8eef6;
  border-collapse: collapse;
  table-layout: fixed;
}

.member-table tr {
  border-bottom: 1px solid #e8eef6;
}

.member-table tr:last-child {
  border-bottom: 0;
}

.member-table td {
  height: 72px;
  border-right: 1px solid #e8eef6;
  padding: 12px 16px;
  color: #596980;
  font-size: 13px;
  vertical-align: middle;
}

.member-table td:last-child {
  border-right: 0;
}

.member-table__identity {
  width: 24%;
}

.member-table__phone {
  width: 22%;
}

.member-table__email {
  width: 32%;
}

.member-table__image {
  width: 22%;
}

.member-cell {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.member-cell i {
  flex: 0 0 auto;
  color: var(--primary);
  font-size: 16px;
}

.member-cell span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.member-cell img {
  width: 56px;
  height: 56px;
  border: 1px solid #edf1f7;
  border-radius: 4px;
  object-fit: cover;
}

.member-table-wrapper::-webkit-scrollbar {
  height: 8px;
}

.member-table-wrapper::-webkit-scrollbar-track {
  border-radius: 4px;
  background: #f0f3f8;
}

.member-table-wrapper::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: #cfd8e5;
}

@media (max-width: 1320px) {
  .technical-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1024px) {
  .content-grid,
  .content-grid--three {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 768px) {
  .panel-chart--country {
    height: 400px;
  }
}

@media (max-width: 720px) {
  .summary-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .summary-card {
    min-height: 330px;
  }

  .technical-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .panel-head--table {
    align-items: flex-start;
    flex-direction: column;
  }

  .panel-head__tools {
    width: 100%;
    justify-content: space-between;
  }
}

@media (max-width: 420px) {
  .metric-grid--three {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
  }

  .metric-grid--three .metric {
    display: grid;
    grid-template-columns: minmax(74px, 1fr) auto;
    align-items: center;
    border-top: 1px solid #edf1f7;
    border-left: 0;
    padding: 9px 0 0;
  }

  .metric-grid--three .metric:first-child {
    border-top: 0;
    padding-top: 0;
  }

  .metric-grid--three .metric-change {
    grid-column: 2;
  }

  .metric-grid--three .metric-value {
    margin-top: 0;
    text-align: right;
  }

  .summary-card {
    min-height: 430px;
  }

  .technical-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media print {
  .panel,
  .summary-card {
    break-inside: avoid;
    box-shadow: none;
  }

  .table-action,
  .panel-head__tools .el-radio-group {
    display: none;
  }
}
</style>
